import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
  CircularProgress, Snackbar, Alert,
} from '@mui/material';
import useShipDate from '../setting/shipDate/useShipDate';
import PlatformArrivalService from './PlatformArrivalService';

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('th-TH', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return String(d); }
};

const StatusChip = ({ row }) => {
  if (row.sentStatus === 'sent') {
    return <Chip size="small" color="success" label={`ส่งแล้ว${row.sentChannel ? ` (${row.sentChannel})` : ''}`} />;
  }
  if (row.sentStatus === 'failed') {
    return <Chip size="small" color="error" label="ล้มเหลว" />;
  }
  return <Chip size="small" variant="outlined" label="ยังไม่ส่ง" />;
};

const SummaryCard = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-gray-50 text-gray-800',
    good: 'bg-green-50 text-green-700',
    warn: 'bg-amber-50 text-amber-700',
    bad: 'bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-lg px-4 py-3 ${tones[tone]} min-w-[120px]`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
};

export default function PlatformArrival() {
  const shopName = useSelector((state) => state.shop.name);
  const { shipDates, getShipDates } = useShipDate();

  const [shipDateId, setShipDateId] = useState('');
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, note, run }
  const [toast, setToast] = useState(null); // { severity, message }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { getShipDates(); }, []);

  const customers = useMemo(() => summary?.customers || [], [summary]);

  // Default selection when a summary loads: reachable customers not yet sent.
  const defaultSelection = (rows) =>
    new Set(rows.filter((c) => c.reachable && c.sentStatus !== 'sent').map((c) => c.customerId));

  const loadSummary = async (id) => {
    if (!id) { setSummary(null); setSelected(new Set()); return; }
    setLoading(true);
    try {
      const res = await PlatformArrivalService.getPlatformArrivalSummary({ shipDateId: id, shop: shopName });
      setSummary(res.data);
      setSelected(defaultSelection(res.data.customers || []));
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ' });
      setSummary(null);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  };

  const onRoundChange = (e) => {
    const id = e.target.value;
    setShipDateId(id);
    loadSummary(id);
  };

  const toggle = (customerId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId); else next.add(customerId);
      return next;
    });
  };

  const selectableIds = useMemo(
    () => customers.filter((c) => c.reachable && c.sentStatus !== 'sent').map((c) => c.customerId),
    [customers]
  );
  const allSelectableChosen = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleAll = () => {
    setSelected(allSelectableChosen ? new Set() : new Set(selectableIds));
  };

  const selectedList = customers.filter((c) => selected.has(c.customerId));

  const showResult = (data) => {
    const parts = [`ส่งสำเร็จ ${data.totalSent} คน`];
    if (data.totalFailed) parts.push(`ล้มเหลว ${data.totalFailed}`);
    if (data.skippedAlreadySent?.length) parts.push(`ข้าม(ส่งแล้ว) ${data.skippedAlreadySent.length}`);
    if (data.skippedUnreachable?.length) parts.push(`ข้าม(ไม่มี LINE) ${data.skippedUnreachable.length}`);
    if (data.invalidIds?.length) parts.push(`ไม่อยู่ในรอบ ${data.invalidIds.length}`);
    setToast({ severity: data.totalFailed ? 'warning' : 'success', message: parts.join(' · ') });
  };

  const runSend = async (fn, customerIds, force = false) => {
    setSending(true);
    try {
      const res = await fn({ shipDateId, shop: shopName, customerIds, force });
      showResult(res.data);
      await loadSummary(shipDateId);
    } catch (err) {
      setToast({ severity: 'error', message: err.response?.data?.message || 'ส่งไม่สำเร็จ' });
    } finally {
      setSending(false);
      setConfirm(null);
    }
  };

  const confirmBroadcast = () => {
    const ids = [...selected];
    setConfirm({
      title: 'ยืนยันส่งผ่าน Chatcone',
      note: `จะส่งแจ้งเตือนของถึงให้ลูกค้า ${ids.length} คนที่เลือก (ระบบจะข้ามคนที่ส่งไปแล้วโดยอัตโนมัติ)`,
      names: selectedList,
      run: () => runSend(PlatformArrivalService.broadcastPlatformArrival, ids, false),
    });
  };

  const confirmSendLine = () => {
    const ids = [...selected];
    setConfirm({
      title: 'ยืนยันส่งผ่าน LINE (กรณี Chatcone ล่ม)',
      note: `จะส่งผ่าน LINE โดยตรงให้ลูกค้า ${ids.length} คนที่เลือก (ข้ามคนที่ส่งไปแล้ว)`,
      names: selectedList,
      run: () => runSend(PlatformArrivalService.sendPlatformArrivalViaLine, ids, false),
    });
  };

  const confirmResend = (row) => {
    setConfirm({
      title: 'ส่งซ้ำให้ลูกค้าคนนี้?',
      note: `ลูกค้า "${row.contact}" ถูกทำเครื่องหมายว่าส่งแล้ว การส่งซ้ำจะส่งข้อความอีกครั้ง`,
      names: [row],
      run: () => runSend(PlatformArrivalService.broadcastPlatformArrival, [row.customerId], true),
    });
  };

  const s = summary?.summary;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-xl font-semibold mb-1">แจ้งเตือนของถึง — ลูกค้าแพลตฟอร์ม (แอป)</h1>
      <p className="text-sm text-gray-500 mb-4">
        ร้าน: <b>{shopName}</b> · ส่งผ่าน Chatcone เป็นหลัก และมีปุ่มส่งผ่าน LINE สำรองสำหรับรายที่ยังไม่ได้ส่ง
      </p>

      <div className="flex items-center gap-3 mb-4">
        <FormControl size="small" style={{ minWidth: 280 }}>
          <InputLabel>เลือกรอบ</InputLabel>
          <Select label="เลือกรอบ" value={shipDateId} onChange={onRoundChange}>
            <MenuItem value=""><em>—</em></MenuItem>
            {shipDates?.map((sd) => (
              <MenuItem key={sd.id} value={sd.id}>
                {formatDate(sd.shipDate)} — {sd.isAir ? 'รอบบิน ✈️' : 'รอบเรือ 🚢'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={22} />}
      </div>

      {s && (
        <div className="flex flex-wrap gap-3 mb-4">
          <SummaryCard label="ลูกค้าทั้งหมด" value={s.totalCustomers} />
          <SummaryCard label="พัสดุ" value={s.totalParcels} />
          <SummaryCard label="ส่งได้ (มี LINE)" value={s.reachable} tone="good" />
          <SummaryCard label="ส่งแล้ว" value={s.sent} tone="good" />
          <SummaryCard label="ยังไม่ส่ง" value={s.notSent} tone="warn" />
          <SummaryCard label="ไม่มี LINE" value={s.unreachable} tone="bad" />
        </div>
      )}

      {summary && customers.length > 0 && (
        <>
          <div className="flex items-center gap-3 mb-3">
            <Button variant="contained" disabled={sending || selected.size === 0} onClick={confirmBroadcast}>
              ส่งผ่าน Chatcone ({selected.size})
            </Button>
            <Button variant="outlined" color="secondary" disabled={sending || selected.size === 0} onClick={confirmSendLine}>
              ส่งผ่าน LINE สำรอง ({selected.size})
            </Button>
            {sending && <CircularProgress size={22} />}
          </div>

          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b bg-gray-50">
                <th className="py-2 px-2 w-10">
                  <Checkbox size="small" checked={allSelectableChosen} onChange={toggleAll}
                    disabled={selectableIds.length === 0} />
                </th>
                <th className="py-2 px-2">ลูกค้า</th>
                <th className="py-2 px-2 text-center">พัสดุ</th>
                <th className="py-2 px-2 text-center">LINE</th>
                <th className="py-2 px-2 text-center">สถานะ</th>
                <th className="py-2 px-2 text-center">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((row) => {
                const selectable = row.reachable && row.sentStatus !== 'sent';
                return (
                  <tr key={row.customerId} className="border-b hover:bg-gray-50">
                    <td className="py-1 px-2">
                      <Checkbox size="small"
                        checked={selected.has(row.customerId)}
                        disabled={!selectable}
                        onChange={() => toggle(row.customerId)} />
                    </td>
                    <td className="py-1 px-2">{row.contact}</td>
                    <td className="py-1 px-2 text-center">{row.parcelCount}</td>
                    <td className="py-1 px-2 text-center">
                      {row.reachable
                        ? <span className="text-green-600">✓</span>
                        : <span className="text-red-500" title="ยังไม่ได้เชื่อม LINE / ยังไม่ได้เพิ่มเพื่อน">✗</span>}
                    </td>
                    <td className="py-1 px-2 text-center"><StatusChip row={row} /></td>
                    <td className="py-1 px-2 text-center">
                      {row.sentStatus === 'sent' && row.reachable && (
                        <Button size="small" color="warning" disabled={sending} onClick={() => confirmResend(row)}>
                          ส่งซ้ำ
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {summary && customers.length === 0 && !loading && (
        <p className="text-gray-500">ไม่มีลูกค้าแพลตฟอร์มในรอบนี้</p>
      )}

      <Dialog open={!!confirm} onClose={() => !sending && setConfirm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{confirm?.title}</DialogTitle>
        <DialogContent dividers>
          <p className="mb-3">{confirm?.note}</p>
          {confirm?.names?.length > 0 && (
            <div className="max-h-56 overflow-auto text-sm border rounded p-2 bg-gray-50">
              {confirm.names.map((c) => (
                <div key={c.customerId} className="flex justify-between py-0.5">
                  <span>{c.contact}</span>
                  <span className="text-gray-400">{c.parcelCount} พัสดุ</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={sending} onClick={() => setConfirm(null)}>ยกเลิก</Button>
          <Button variant="contained" disabled={sending} onClick={() => confirm?.run()}>
            {sending ? 'กำลังส่ง…' : 'ยืนยันส่ง'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>}
      </Snackbar>
    </div>
  );
}
